import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Modal,
  Button,
  FormGroup,
  ControlLabel,
  Badge,
  FormControl,
  Collapse,
  OverlayTrigger,
  Popover,
} from 'react-bootstrap';
import './_edit.menu.scss'
import './_edit.menu.dark-mode.scss'
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import AlertComponent from '../../../../../../../components/Alert';

import { getErrorMessage } from '../../../../../../../utils/helper';
import SelectIcon from '../../../../../../../components/SelectIcon';
import { ICON_TYPES } from '../../../../../../../components/SelectIcon/constants';
import Spinner from '../../../../../../../components/Loaders/Spinner';
import Translations from '../../../../../../../components/Collapse/Translations';
import { createLanguagesData } from './helper';
import { menuCreate, menuDelete, menuEdit, menuPages } from '../../../../../../../services/endpoints/menu';
import { pagesLanguagesActive } from '../../../../../../../services/endpoints/pages';

const EditMenuModal = ({ data, onHide, onSubmit }) => {
  let popoverRef = useRef(null);
  const [error, setError] = useState(null);
  const [isOpenTranslations, setOpenTranslations] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [modalData, setModalData] = useState({
    init: false,
    form: {
      title: {},
      public: 0,
      is_external: 0,
      url: '',
      icon: '',
    },
    pages: [],
    default_language: null,
    languages: [],
  })
  const { default_language, languages, pages, form, init } = modalData;

  const defaultLanguage = useMemo(() => languages.find((language) => language.code === default_language), [default_language, languages])
  const anotherLanguages = useMemo(() => languages.filter((language) => language.code !== default_language), [default_language, languages])
  const isEdit = !!data?.id;

  const onDeleteItem = async () => {
    popoverRef?.handleHide();
    setLoading(true);
    try {
      await menuDelete(data?.id)
      onSubmit()
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      setLoading(false);
    }
  };

  const getAllData = async () => {
    try {
      const pagesParams = {
        is_public: data?.menuType === 'public' ? 1 : 0,
      };
      if (isEdit) {
        pagesParams.menu_id = data.id;
      }
      const [respPages, respLanguages] = await Promise.all([menuPages(pagesParams), pagesLanguagesActive()]);
      const languagesData = createLanguagesData(respLanguages.data.languages);
      const pages = respPages.data.page;
      if (!isEmpty(respPages.data.selected)) {
        pages.push(respPages.data.selected)
      }
      setModalData((prevState) => {
        const isExternal = data?.external_link ? 1 : 0;
        const firstPage = pages.length ? pages[0] : null;
        return {
          ...prevState,
          init: true,
          pages,
          form: {
            ...prevState.form,
            page_id: isEdit ? (data?.page_id || null) : (firstPage ? firstPage.id : null),
            is_external: isExternal,
            url: isExternal ? data.external_link : '',
            title: isEdit ? data.title : (firstPage ? { ...languagesData.codes, [data.default_language]: firstPage.name } : languagesData.codes),
            public: data.menuType === 'public' ? 1 : 0,
            icon: data?.icon || '',
          },
          default_language: data.default_language,
          languages: respLanguages.data.languages,
        }
      })
    } catch (error) {
      setModalData((prevState) => ({
        ...prevState,
        init: false,
      }))
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
    }
  }

  useEffect(() => {
    getAllData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSubmitForm = async () => {
    setLoading(true);
    try {
      isEdit ? await menuEdit(data.id, form) : await menuCreate(form)
      onSubmit()
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      setLoading(false);
    }
  };

  const onChangePageLink = (value) => {
    const pageSelected = pages.find((page) => page.id === Number(value));
    setModalData((prevState) => ({
      ...prevState,
      form: {
        ...prevState.form,
        url: '',
        page_id: value === 0 ? null : value,
        is_external: value === 0 ? 1 : 0,
        title: {
          ...prevState.form.title,
          [default_language]: pageSelected ? pageSelected.name : '',
        },
      },
    }))
  }

  const onChangeExternalLink = (value) => {
    setModalData((prevState) => ({
      ...prevState,
      form: {
        ...prevState.form,
        url: value,
      },
    }))
  }

  const onChangeName = (value, code) => {
    setModalData((prevState) => ({
      ...prevState,
      form: {
        ...prevState.form,
        title: {
          ...prevState.form.title,
          [code]: value,
        },
      },
    }))
  }

  const onChangeIcon = (value) => {
    setModalData((prevState) => ({
      ...prevState,
      form: {
        ...prevState.form,
        icon: value || '',
      },
    }))
  }

  const popover = (
    <Popover id="popover-trigger-click-root-close" title="" className="text-center">
      <Button bsStyle="primary" bsSize="sm" onClick={onDeleteItem}>
        Delete
      </Button>
      <Button bsSize="sm" className="ml-8" onClick={() => popoverRef?.handleHide()}>
        Cancel
      </Button>
    </Popover>
  );

  return (
    <>
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit ? 'Edit' : 'Add'}
          {' '}
          menu item
        </Modal.Title>
      </Modal.Header>
      {!init
        ? (
          <>
            <Modal.Body>
              {error ? <AlertComponent text={error} type="danger" /> : <Spinner />}
            </Modal.Body>
            {error ? (
              <Modal.Footer className="custom__modal-footer">
                <Button
                  disabled={isLoading}
                  onClick={onHide}
                >
                  Close
                </Button>
              </Modal.Footer>
            ) : null }
          </>
        )
        : (
          <>
            <Modal.Body>
              {isLoading && <div className="modal-loader-small" />}
              {error && <AlertComponent text={error} type="danger" />}
              <FormGroup
                className={classNames({
                  'form-group__rtl': defaultLanguage.direction === 'rtl',
                })}
              >
                <ControlLabel htmlFor={`page-name-${defaultLanguage.code}`}>
                  Name
                  {' '}
                  <Badge>{defaultLanguage.name}</Badge>
                </ControlLabel>
                <Translations onOpen={() => setOpenTranslations(!isOpenTranslations)} open={isOpenTranslations} items={languages}>
                  <FormControl
                    type="text"
                    id={`page-name-${defaultLanguage.code}`}
                    name={`page_name_${defaultLanguage.code}`}
                    value={form.title[defaultLanguage.code]}
                    onChange={(e) => onChangeName(e.target.value, defaultLanguage.code)}
                    required
                  />
                </Translations>
              </FormGroup>
              <Collapse in={isOpenTranslations}>
                <div>
                  {anotherLanguages.length ? anotherLanguages.map((language) => (
                    <div key={`page-translation-${language.code}`}>
                      <FormGroup
                        className={classNames({
                          'form-group__rtl': language.direction === 'rtl',
                        })}
                      >
                        <ControlLabel htmlFor={`page-name-${language.code}`}>
                          Name
                          {' '}
                          <Badge>{language.name}</Badge>
                        </ControlLabel>
                        <FormControl
                          type="text"
                          id={`page-name-${language.code}`}
                          name={`page_name_${language.code}`}
                          value={form.title[language.code]}
                          onChange={(e) => onChangeName(e.target.value, language.code)}
                          required
                        />
                      </FormGroup>
                    </div>
                  )) : null}
                  <hr />
                </div>
              </Collapse>
              <FormGroup controlId="page_link">
                <ControlLabel>Page link</ControlLabel>
                <FormControl
                  value={form.page_id || 0}
                  onChange={(e) => onChangePageLink(Number(e.target.value))}
                  componentClass="select"
                  name="EditMenuForm[page_id]"
                  placeholder="select"
                >
                  <option value="" disabled>
                    Select page
                  </option>
                  {pages.map((page) => (
                    <option key={`page-${page.id}`} value={page.id}>{page.name}</option>
                  ))}
                  <option value="0">
                    External link
                  </option>
                </FormControl>
              </FormGroup>
              {!form.page_id
                ? (
                  <FormGroup>
                    <ControlLabel htmlFor="page-url">
                      Page url
                    </ControlLabel>
                    <FormControl
                      type="text"
                      id="page-url"
                      name="EditMenuForm[url]"
                      value={form.url}
                      onChange={(e) => onChangeExternalLink(e.target.value)}
                    />
                  </FormGroup>
                ) : null}
              <FormGroup>
                <div className="edit-menu__icon">
                  <div className="edit-menu__icon-preview">
                    <SelectIcon
                      types={[ICON_TYPES.ICON]}
                      extensions={[]}
                      icon={form.icon ? {
                        icon_type: ICON_TYPES.ICON,
                        icon: form.icon,
                      } : null}
                      onChange={(value) => onChangeIcon(value?.icon)}
                    />
                  </div>
                  <div className="edit-menu__icon-input">
                    <ControlLabel htmlFor="menu-icon">
                      Icon
                    </ControlLabel>
                    <FormControl
                      type="text"
                      id="menu-icon"
                      name="EditMenuForm[icon]"
                      value={form.icon}
                      placeholder="fab fa-500px"
                      onChange={(e) => onChangeIcon(e.target.value)}
                    />
                  </div>
                </div>
                <div className="edit-menu__icon-help">
                  Select icon or paste icon class from
                  {' '}
                  <a href="/anon.ws?r=https://fontawesome.com/v5/search" target="_blank">FontAwesome5</a>
                </div>
              </FormGroup>

            </Modal.Body>
            <Modal.Footer className="custom__modal-footer">
              <Button
                disabled={isLoading}
                bsStyle="primary"
                type="submit"
                onClick={onSubmitForm}
              >
                <span>
                  {isEdit ? 'Edit' : 'Add'}
                  {' '}
                  menu item
                </span>
              </Button>
              <Button
                disabled={isLoading}
                onClick={onHide}
              >
                Cancel
              </Button>
              {isEdit
                ? (
                  <div className="pull-right" style={{ position: 'relative' }}>
                    <OverlayTrigger ref={(r) => (popoverRef = r)} trigger="click" rootClose placement="top" overlay={popover}>
                      <Button bsStyle="link" className="modal-delete-btn" disabled={isLoading}>
                        <span>Delete</span>
                      </Button>
                    </OverlayTrigger>
                  </div>
                ) : null }
            </Modal.Footer>
          </>
        )}
    </>
  );
};

EditMenuModal.propTypes = {
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  onHide: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default EditMenuModal;
